'use client';

import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AlertCircle, X} from 'lucide-react';
import {LocalizationKeys} from '@/lib/all-localization-keys';
import {parseMessageWithLinks} from '@/lib/text-utils';
import {Button} from '@/components/ui/button';

/**
 * WarningBanner component
 *
 * Displays a yellow warning banner at the top of the site when WARNING_MESSAGE_BANNER
 * localization key has a non-blank value. Automatically hyperlinks any URLs in the message.
 * User can close the banner with the X button - banner reappears on page reload.
 */
export function WarningBanner() {
  const {t} = useTranslation();
  const warningMessage = t(LocalizationKeys.WARNING_MESSAGE_BANNER);
  const [isClosed, setIsClosed] = useState(false);

  // Don't render anything if the warning message is empty or banner is closed
  if (isClosed || !warningMessage || warningMessage.trim() === '' || warningMessage.trim() === 'WARNING_MESSAGE_BANNER') {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] w-full bg-yellow-50 dark:bg-yellow-950 border-b border-yellow-200 dark:border-yellow-800">
      <div className="w-full py-3 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <AlertCircle
              className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0"
              aria-hidden="true"
            />
            <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              <p className="mb-1">
                {parseMessageWithLinks(warningMessage)}
              </p>
              <p className="text-xs italic">
                {t(LocalizationKeys.WARNING_BANNER_CLOSE_INSTRUCTION)}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsClosed(true)}
            className="flex-shrink-0 h-8 w-8 p-0 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-900 rounded-full"
            aria-label="Close warning banner"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

